import type { GetInvoicesResponse, Invoice, Project } from "@nlc/shared-types";
import type { UseQueryResult } from "@tanstack/react-query";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { subscriptionKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";

const MOCK_INVOICE_COUNT = 24;

interface UseInvoicesParams {
  limit: number;
  page: number;
  projectId: number;
}

export function useInvoices({
  limit,
  page,
  projectId,
}: UseInvoicesParams): UseQueryResult<GetInvoicesResponse> {
  const { data: userData } = useUser();

  return useQuery<GetInvoicesResponse>({
    enabled: !!userData,
    placeholderData: keepPreviousData,
    queryFn: () => {
      const project = userData!.projects.find((p) => p.id === projectId);

      if (!project) {
        throw new Error("Project not found");
      }

      return paginateInvoices(buildMockInvoices(project), page, limit);
    },
    queryKey: subscriptionKeys.invoices(projectId, page, limit),
  });
}

export function buildMockInvoices(
  project: Pick<Project, "id" | "nextBillingDate" | "price" | "product">,
): Invoice[] {
  const latest = dayjs(project.nextBillingDate).subtract(1, "month");

  return Array.from({ length: MOCK_INVOICE_COUNT }, (_, index) => {
    const date = latest.subtract(index, "month");
    const sequence = String(MOCK_INVOICE_COUNT - index).padStart(6, "0");

    return {
      currency: "USD",
      date: date.toISOString(),
      description: `${project.product} - Fully-managed - 5 design slots`,
      id: `INV-${date.format("YYYYMMDD")}-${sequence}-${project.id}`,
      status: index === 0 ? "overdue" : "paid",
      total: project.price,
    };
  });
}

export function paginateInvoices(
  invoices: Invoice[],
  page: number,
  limit: number,
): GetInvoicesResponse {
  const start = (page - 1) * limit;

  return {
    items: invoices.slice(start, start + limit),
    pagination: {
      limit,
      page,
      totalPages: Math.ceil(invoices.length / limit),
    },
    total: invoices.length,
  };
}
